
import Vue from 'vue';
import BaseIcon from '@core/components/UI/BaseIcon.vue';
import { HeadlineWithRelatedArticles } from '@headlines/types/headline';
import { gridConfig } from '@core/config/grid.config';

interface Props {
  headline: HeadlineWithRelatedArticles;
  onTop: boolean;
  size: string;
}

interface Data {
  iconList: Record<string, string>;
  contentType: {
    career: boolean;
    detector: boolean;
    detectorCategory: boolean;
    video: boolean;
    gallery: boolean;
    stream: boolean;
    streamSoon: boolean;
    audio: boolean;
    trending: boolean;
    exclusive: boolean;
    research: boolean;
    bloomberg: boolean;
    economist: boolean;
    politicalAd: boolean;
  };
}

interface Computed {
  classModifier: Record<string, any>;
  isMobileView: boolean;
}

interface Methods {
  hasTagId: (tagId: number) => boolean;
  isPrimaryCategory: (categoryId: number) => boolean;
  isNoPadding: (name: string) => boolean;
}

const iconList = {
  gallery: 'gallery',
  video: 'play',
  audio: 'microphone',
  trending: 'trending',
};

interface Process {
  client: boolean;
}

declare let process: Process;

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BaseIcon,
  },
  props: {
    headline: {
      type: Object,
      required: true,
    },
    onTop: {
      type: Boolean,
      default: true,
      required: false,
    },
    size: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      iconList,
      contentType: {
        career: !!this.headline.tags?.items?.find((tag) => [89580921, 89580923, 89580941, 89580979, 89580981, 89969303].includes(tag.id)),
        detector: this.headline.primaryCategory.parentCategory.id === 85827763 || this.headline.primaryCategory.parentCategory.id === 79440681,
        detectorCategory: [
          85827865, 79451385, 79451419, 85827867, 88121525, 88122145, 92718855, 92719795, 93953369, 93953457, 93481599, 92782775, 85827871, 79451335,
        ].includes(this.headline.primaryCategory?.id),
        video: this.headline.relatedEntities?.hasVideo && this.headline.primaryCategory?.channel.url !== 'www.delfi.lt/video',
        gallery: this.headline.relatedEntities?.hasGallery || (this.headline.settings?.find((setting) => setting.key === 'icon.image')?.value ?? false),
        stream: this.headline.relatedEntities?.hasStream,
        streamSoon: this.headline.settings?.find((setting) => setting.key === 'streamSoon')?.value ?? false,
        sustainableCourse: !!this.headline.tags?.items?.find((tag) => [91160101].includes(tag.id)),
        audio: this.headline.relatedEntities?.hasAudio,
        trending: this.headline.settings?.find((setting) => setting.key === 'trending')?.value ?? false,
        exclusive: this.headline.settings?.find((setting) => setting.key === 'icon.exclusive')?.value ?? false,
        research: this.headline.settings?.find((setting) => setting.key === 'icon.research')?.value ?? false,
        bloomberg:
          this.headline.primaryCategory.id === 79298779 ||
          !!this.headline.tags?.items?.find((tag) => tag.id === 82146369) ||
          (this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
            this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'Bloomberg'),
        economist:
          !!this.headline.tags?.items?.find((tag) => tag.id === 49711978) ||
          (this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
            this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'The Economist'),
        politicalAd:
          [84666519, 85038351, 80167949, 91841171].includes(this.headline.primaryCategory?.id) ||
          !!this.headline.tags?.items?.find((tag) => tag.id === 71865184),
      },
    };
  },
  computed: {
    classModifier() {
      return {
        'C-headline-icons--on-top': this.onTop,
        'C-headline-icons--small': this.contentType.streamSoon,
        [`C-headline-icons--${this.size}`]: !!this.size,
      };
    },
    isMobileView() {
      if (process.client) {
        return window.screen.width <= gridConfig.breakpoints.lg;
      } else {
        return this.$ua && this.$ua.deviceType() === 'smartphone' && !this.$ua.isFromTablet();
      }
    },
  },
  methods: {
    hasTagId(tagId) {
      return !!this.headline.tags?.items?.find((tag) => tag.id === tagId);
    },
    isPrimaryCategory(categoryId) {
      return this.headline.primaryCategory.id === categoryId;
    },
    isNoPadding(name) {
      const noPaddingIcons: string[] = ['career', 'exclusive', 'political', 'sustainableCourse', 'research'];

      return this.isMobileView && noPaddingIcons.includes(name);
    },
  },
});
